/**
 * Mapeia falhas do fluxo 3DS (SDK Stone/Pagar.me, status do emissor, rede)
 * para mensagens amigáveis + um código de suporte estável.
 * O código aparece no modal para o cliente e é gravado no action_logs
 * (prefixando threeds_message), permitindo correlacionar print <-> banco.
 */

export const THREEDS_CODES = {
  TOKEN_MISSING: 'E-3DS-001',
  UNSUPPORTED_BRAND: 'E-3DS-002',
  CONTAINERS_MISSING: 'E-3DS-003',
  SDK_NOT_LOADED: 'E-3DS-004',
  CARD_UNAUTHORIZED: 'E-3DS-101',
  AUTH_DENIED: 'E-3DS-102',
  CHALLENGE_INCOMPLETE: 'E-3DS-103',
  CHALLENGE_CANCELED: 'E-3DS-104',
  INVALID_CUSTOMER_DATA: 'E-3DS-105',
  NETWORK: 'E-3DS-201',
  TIMEOUT: 'E-3DS-202', // A palavra "Timeout" é o que faz o threeDSErrorMapper classificar como E-3DS-202.
  UNKNOWN_STATUS: 'E-3DS-301',
  UNKNOWN: 'E-3DS-999',
};

const CATALOG = {
  [THREEDS_CODES.TOKEN_MISSING]: {
    title: 'Não foi possível iniciar a verificação',
    message:
      'Não conseguimos iniciar a verificação de segurança do seu cartão. Aguarde alguns segundos e tente novamente.',
    supportHint:
      'Backend não retornou threeds_token. Verificar store.use_threeds e criação do payment_process. Pedir nova tentativa.',
    retryable: true,
  },
  [THREEDS_CODES.UNSUPPORTED_BRAND]: {
    title: 'Cartão não compatível com a verificação',
    message:
      'A bandeira deste cartão não é compatível com a verificação de segurança desta loja. Finalize com um cartão Visa ou Mastercard.',
    supportHint:
      'Bandeira fora de threeds_supported_brands. Orientar Visa/Master.',
    retryable: true,
  },
  [THREEDS_CODES.CONTAINERS_MISSING]: {
    title: 'Falha ao abrir a verificação',
    message:
      'Não foi possível abrir a tela de verificação. Atualize a página e tente novamente.',
    supportHint:
      'Containers #tdsMethodContainer/#challengeContainer ausentes no DOM.',
    retryable: true,
  },
  [THREEDS_CODES.SDK_NOT_LOADED]: {
    title: 'Falha ao carregar a verificação',
    message:
      'Não foi possível carregar a verificação de segurança. Verifique sua conexão, atualize a página e tente novamente.',
    supportHint:
      'window.TDS indisponível (script 3DS não carregou). Ver CSP/script-src e rede do cliente.',
    retryable: true,
  },
  [THREEDS_CODES.CARD_UNAUTHORIZED]: {
    title: 'Cartão não autorizado para verificação',
    message:
      'Este cartão não pôde ser autenticado pelo banco emissor. Ele pode não estar habilitado para compras online com verificação de segurança. Tente finalizar com outro cartão de crédito (Visa ou Mastercard).',
    supportHint:
      'SDK retornou "the card is unauthorized". Cartão não participa/não elegível ao 3DS. Orientar uso de outro cartão Visa/Master habilitado para compras online.',
    retryable: true,
  },
  [THREEDS_CODES.AUTH_DENIED]: {
    title: 'Verificação negada pelo banco',
    message:
      'O banco emissor não autorizou esta compra na verificação de segurança. Entre em contato com seu banco ou tente outro cartão.',
    supportHint:
      'trans_status N/R (negado pelo emissor). Cliente fala com o banco ou usa outro cartão.',
    retryable: true,
  },
  [THREEDS_CODES.CHALLENGE_INCOMPLETE]: {
    title: 'Verificação não concluída',
    message:
      'A verificação de segurança do seu banco não foi concluída. Refaça o pagamento e confirme a verificação (app do banco ou SMS).',
    supportHint:
      'trans_status C sem conclusão. Cliente abriu o challenge mas não finalizou.',
    retryable: true,
  },
  [THREEDS_CODES.CHALLENGE_CANCELED]: {
    title: 'Verificação cancelada',
    message:
      'A verificação de segurança foi cancelada. Tente novamente e conclua a confirmação do seu banco.',
    supportHint: 'Cliente fechou/cancelou o challenge.',
    retryable: true,
  },
  [THREEDS_CODES.NETWORK]: {
    title: 'Falha de conexão na verificação',
    message:
      'Houve uma falha de conexão durante a verificação de segurança. Verifique sua internet e tente novamente.',
    supportHint:
      'Erro de rede / HTTP 502-503-504 durante o 3DS. Provável instabilidade momentânea.',
    retryable: true,
  },
  [THREEDS_CODES.TIMEOUT]: {
    title: 'A verificação demorou demais',
    message:
      'A verificação de segurança demorou mais que o esperado. Tente novamente.',
    supportHint:
      'Timeout (DDC/challenge). Comum no Safari iOS com bloqueio de storage de terceiros.',
    retryable: true,
  },
  [THREEDS_CODES.INVALID_CUSTOMER_DATA]: {
    title: 'Dados do cliente inválidos',
    message:
      'Não foi possível iniciar a verificação porque há um dado inválido no cadastro (normalmente o e-mail). Confira o e-mail informado e tente novamente.',
    supportHint:
      'SDK rejeitou campo do orderData (ex.: e-mail com acento). Corrigir cadastro do cliente.',
    retryable: true,
  },
  [THREEDS_CODES.UNKNOWN_STATUS]: {
    title: 'Não foi possível concluir a verificação',
    message:
      'Não foi possível concluir a verificação de segurança. Tente novamente ou use outro cartão.',
    supportHint:
      'trans_status fora do mapeamento. Ver action_logs (response) para o status bruto.',
    retryable: true,
  },
  [THREEDS_CODES.UNKNOWN]: {
    title: 'Não foi possível concluir a verificação',
    message:
      'Não foi possível concluir a verificação de segurança. Tente novamente ou use outro cartão.',
    supportHint:
      'Erro 3DS não classificado. Ver sendThreeDSDebug / action_logs.',
    retryable: true,
  },
};

const errText = err => {
  if (!err) return '';
  if (typeof err === 'string') return err;
  return String(err.message || err.detail || '');
};

function pickCode({
  error,
  httpStatus,
  transStatus,
  action,
  internalStatus,
  challengeCanceled,
} = {}) {
  if (internalStatus === 'TOKEN_MISSING') return THREEDS_CODES.TOKEN_MISSING;
  if (internalStatus === 'SKIPPED_UNSUPPORTED_BRAND')
    return THREEDS_CODES.UNSUPPORTED_BRAND;
  if (internalStatus === 'CONTAINERS_MISSING')
    return THREEDS_CODES.CONTAINERS_MISSING;

  const text = errText(error).toLowerCase();

  if (/must be a valid email|invalid email|acctnumber|acct_number/i.test(text))
    return THREEDS_CODES.INVALID_CUSTOMER_DATA;
  if (/unauthorized|not authorized|n[aã]o autorizad/.test(text))
    return THREEDS_CODES.CARD_UNAUTHORIZED;
  if (/tdsmethodcontainer|challengecontainer|containers?\s+3ds/.test(text))
    return THREEDS_CODES.CONTAINERS_MISSING;
  if (/window\.tds|tds n[aã]o|sdk.*(load|carreg)|script 3ds/.test(text))
    return THREEDS_CODES.SDK_NOT_LOADED;
  if (/timeout|timed out|demorou/.test(text)) return THREEDS_CODES.TIMEOUT;
  if (
    /network error|failed to fetch|err_network/.test(text) ||
    [502, 503, 504].includes(Number(httpStatus))
  )
    return THREEDS_CODES.NETWORK;

  if (action === 'block') return THREEDS_CODES.AUTH_DENIED;
  if (action === 'challenge')
    return challengeCanceled
      ? THREEDS_CODES.CHALLENGE_CANCELED
      : THREEDS_CODES.CHALLENGE_INCOMPLETE;
  if (action === 'unknown' || action === 'default')
    return THREEDS_CODES.UNKNOWN_STATUS;

  const ts = String(transStatus || '').toUpperCase();
  if (['N', 'R'].includes(ts)) return THREEDS_CODES.AUTH_DENIED;
  if (ts === 'C') return THREEDS_CODES.CHALLENGE_INCOMPLETE;
  if (ts && !['Y', 'A', 'U', 'I'].includes(ts))
    return THREEDS_CODES.UNKNOWN_STATUS;

  return THREEDS_CODES.UNKNOWN;
}

export function resolve3DSFailure(signals = {}) {
  const code = pickCode(signals);
  const base = CATALOG[code] || CATALOG[THREEDS_CODES.UNKNOWN];
  return {
    code,
    reference: signals.reference != null ? String(signals.reference) : null,
    transStatus: signals.transStatus || null,
    ...base,
  };
}
